import { fetchData } from "../../../../utils/dataProvider";
import { GET, POST, PUT, ROWS_PER_PAGE } from "../../../../utils/constants";

export interface IInternalClient {
  id: number;
  name: string;
  email: string;
  disabled: boolean;
  apiKey?: string | null;
  cuit?: string;
  businessName?: string;
  entity?: {
    name: string;
    type: "marketplace" | "ecommerce";
  };
}

export interface IClientListResponse {
  clients: IInternalClient[];
  count: number;
}

export interface IRegisterClientInput {
  email: string;
  password: string;
  entity: "marketplace" | "ecommerce";
  country: string;
  securityCode?: string;
  userName: string;
  entityName: string;
  cuit: string;
  contractOwner?: "marketplace" | "merchant";
  businessName?: string;
  ivaSituation?: string;
  commercialAddress?: string;
  webSite?: string;
  billingEmail?: string;
}

export type TClientStatus = "pending" | "accepted";

interface IGetClientsParams {
  status: TClientStatus;
  skip?: number;
  limit?: number;
  search?: string;
}

/**
 * Fetches clients by status with optional search and pagination
 */
export const getClients = async ({
  status,
  skip = 0,
  limit = ROWS_PER_PAGE,
  search,
}: IGetClientsParams): Promise<IClientListResponse> => {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  const { clients, count } = await fetchData({
    url: `/internal/clients/${status}?skip=${skip}&limit=${limit}${searchParam}`,
    method: GET,
  });
  return { clients, count };
};

/**
 * Registers a new client (marketplace or ecommerce) using the full account flow.
 * Returns the apiKey of the newly created client.
 */
export const registerClient = async ({
  entity,
  securityCode,
  ...rest
}: IRegisterClientInput): Promise<{ apiKey: string }> => {
  const { data } = await fetchData({
    url: `/internal/register/client/${entity}`,
    method: POST,
    body: { ...rest, securityCode },
  });
  return data;
};

/**
 * Updates the status (enabled/disabled) of one or more clients
 */
export const updateClientStatus = async (
  clients: Array<{ id: number; disabled: boolean; name?: string }>
): Promise<string> => {
  const { data } = await fetchData({
    url: "/internal/updateClientStatus",
    method: PUT,
    body: { clients },
  });
  return data || "Los clientes han sido actualizados con éxito.";
};
